import { useQuery } from '@tanstack/react-query';
import { Profile, PaginatedResponse } from '../../../shared/types';
import axiosService from '../services/axios.service';

// Query key factory for profile search
export const profileSearchKeys = {
    all: ['profileSearch'] as const,
    search: (crewSlug: string, query: string, page?: number, limit?: number) =>
        [...profileSearchKeys.all, crewSlug, 'search', { query, page, limit }] as const
};

// API functions
const profileSearchApi = {
    searchProfiles: async (
        crewSlug: string,
        query: string,
        page: number = 1,
        limit: number = 20
    ): Promise<PaginatedResponse<Profile>> => {
        const { data } = await axiosService.get<PaginatedResponse<Profile>>(
            `/c/${crewSlug}/profiles/search`,
            {
                params: { q: query, page, limit }
            }
        );
        return data;
    }
};

/**
 * Hook for searching profiles within a crew
 * @param crewSlug - The crew slug
 * @param query - Search query string
 * @param page - Page number (default: 1)
 * @param limit - Results per page (default: 20)
 * @param enabled - Whether the query should run (default: true when query has value)
 */
export const useProfileSearch = (
    crewSlug: string,
    query: string,
    page: number = 1,
    limit: number = 20,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: profileSearchKeys.search(crewSlug, query, page, limit),
        queryFn: () => profileSearchApi.searchProfiles(crewSlug, query, page, limit),
        enabled: !!crewSlug && enabled,
        staleTime: 30000, // Consider data fresh for 30 seconds
        placeholderData: (previousData) => previousData // Keep previous data while fetching
    });
};

/**
 * Hook for quick profile search (dropdown/autocomplete)
 * Returns fewer results and only when query length >= 2
 */
export const useQuickProfileSearch = (
    crewSlug: string,
    query: string,
    enabled: boolean = true
) => {
    const trimmedQuery = query.trim();

    return useQuery({
        queryKey: profileSearchKeys.search(crewSlug, trimmedQuery, 1, 10),
        queryFn: () => profileSearchApi.searchProfiles(crewSlug, trimmedQuery, 1, 10),
        enabled: !!crewSlug && trimmedQuery.length >= 2 && enabled,
        staleTime: 30000
    });
};
